import React, { createContext, useContext, useState, useCallback } from 'react';
import { authFetch } from '../common/authFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RecurringContext = createContext();

export const useRecurring = () => useContext(RecurringContext);

export function RecurringProvider({ children }) {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentRule, setCurrentRule] = useState(null); // rule being edited

    const fetchRules = useCallback(async () => {
        setLoading(true);
        try {
            const res = await authFetch(`${API_URL}/api/v1/recurring-rules`);
            const data = await res.json();
            if (data.success) setRules(data.rules);
        } catch (e) {
            console.error('Failed to fetch recurring rules:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchRule = useCallback(async (ruleId) => {
        try {
            const res = await authFetch(`${API_URL}/api/v1/recurring-rules/${ruleId}`);
            const data = await res.json();
            if (data.success) return data.rule;
        } catch (e) {
            console.error('Failed to fetch rule:', e);
        }
        return null;
    }, []);

    const createRule = useCallback(async (ruleData) => {
        try {
            const res = await authFetch(`${API_URL}/api/v1/recurring-rules`, {
                method: 'POST',
                body: JSON.stringify(ruleData),
            });
            const data = await res.json();
            if (data.success) {
                setRules(prev => [...prev, data.rule]);
                return data.rule;
            }
            return null;
        } catch (e) {
            console.error('Failed to create rule:', e);
            return null;
        }
    }, []);

    const updateRule = useCallback(async (ruleId, ruleData) => {
        try {
            const res = await authFetch(`${API_URL}/api/v1/recurring-rules/${ruleId}`, {
                method: 'PUT',
                body: JSON.stringify(ruleData),
            });
            const data = await res.json();
            if (data.success) {
                setRules(prev => prev.map(r => r.rule_id === ruleId ? data.rule : r));
                return data.rule;
            }
            return null;
        } catch (e) {
            console.error('Failed to update rule:', e);
            return null;
        }
    }, []);

    const deleteRule = useCallback(async (ruleId) => {
        try {
            const res = await authFetch(`${API_URL}/api/v1/recurring-rules/${ruleId}`, {
                method: 'DELETE',
            });
            const data = await res.json();
            if (data.success) {
                setRules(prev => prev.filter(r => r.rule_id !== ruleId));
                return true;
            }
            return false;
        } catch (e) {
            console.error('Failed to delete rule:', e);
            return false;
        }
    }, []);

    const excludeDate = useCallback(async (ruleId, date) => {
        try {
            const res = await authFetch(`${API_URL}/api/v1/recurring-rules/${ruleId}/exclude`, {
                method: 'PUT',
                body: JSON.stringify({ date }),
            });
            const data = await res.json();
            if (data.success) {
                // Refresh the specific rule
                const updated = await fetchRule(ruleId);
                if (updated) {
                    setRules(prev => prev.map(r => r.rule_id === ruleId ? updated : r));
                }
                return data;
            }
            return null;
        } catch (e) {
            console.error('Failed to exclude date:', e);
            return null;
        }
    }, [fetchRule]);

    const previewRule = useCallback(async (ruleData) => {
        try {
            const res = await authFetch(`${API_URL}/api/v1/recurring-rules/preview`, {
                method: 'POST',
                body: JSON.stringify(ruleData),
            });
            const data = await res.json();
            if (data.success) return { dates: data.dates, count: data.count };
            return null;
        } catch (e) {
            console.error('Failed to preview rule:', e);
            return null;
        }
    }, []);

    const detachInstance = useCallback(async (scheduleId) => {
        try {
            const res = await authFetch(`${API_URL}/api/v1/recurring-rules/detach/${scheduleId}`, {
                method: 'PUT',
            });
            const data = await res.json();
            return data.success;
        } catch (e) {
            console.error('Failed to detach instance:', e);
            return false;
        }
    }, []);

    const value = {
        rules,
        loading,
        currentRule,
        setCurrentRule,
        fetchRules,
        fetchRule,
        createRule,
        updateRule,
        deleteRule,
        excludeDate,
        previewRule,
        detachInstance,
    };

    return (
        <RecurringContext.Provider value={value}>
            {children}
        </RecurringContext.Provider>
    );
}
